import React, { useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { map } from 'underscore';
import {
  useDefaultEditorStyle,
  getComponentById,
  useFloatingBannerMessage,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  DataServiceContext,
  useEditorStatusContext,
  useSiteConfig,
  editorStatusLoadingWrapperForPromise,
  useInstrumentationServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  facebookPageInfoFilter,
  importFromFacebook,
  hasFBOAuthError,
} from '@bingads-webui-campaign-react/smart-page-facebook-import-dialog';
import { ItemPicker } from '@bingads-webui-campaign-react/smart-component-card';

import { ActionButton } from '@fluentui/react/lib/Button';
import { MessageBar } from '@fluentui/react/lib/MessageBar';

import { SelectReviewDialog } from './components/select-review-dialog';
import { SmartReviewCard } from './components/smart-review-card';
import { useDataState } from './components/use-data-state';

const MaxReviewsCount = 10;

const getStyles = (_, { palette = {} }) => ({
  reviewsListLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0px 38px 28px 20px',
  },
  reviewsList: {
    paddingTop: 7,
  },
  addBtn: {
    '&:enabled': {
      color: palette.themePrimary,
    },
  },
  noData: {
    background: '#F3F2F0',
    padding: '24px 22px',
  },
});

export const UnStyledSmartReviewEditor = ({
  i18n,
  componentId,
  classes,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    sectionTitleClassName,
    infoMessageBarClassName,
  } = useDefaultEditorStyle();
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const { smartPageService, hostSystemScenario } = useContext(DataServiceContext);
  const [allFbReviews, setAllFbReviews] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const { showWarning } = useFloatingBannerMessage(dispatchEditorStatus);
  const logger = useInstrumentationServiceContext();

  const componentConfig = getComponentById(siteConfig, componentId) || {};
  const [selectedReviews, setSelectedReviews] = useState(componentConfig.reviews);
  const { noDataMsgKey, noData } = useDataState(allFbReviews, selectedReviews);

  const onSaveChange = useCallback(({ newSelectedReviews, imageResult }) => {
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: {
        ...componentConfig,
        reviews: newSelectedReviews,
      },
    });

    dispatchSiteConfig({
      type: 'UPDATE_IMAGES',
      payload: {
        imagesToAdd: imageResult,
      },
    });

    setSelectedReviews(newSelectedReviews);
    setShowDialog(false);
  }, [componentConfig]);

  const onDelete = useCallback((index) => {
    setSelectedReviews((selectedReviewState) => {
      const [deletedReview] = selectedReviewState.splice(index, 1);

      // Reassign the orginal fb src to picture
      // so the deleted review could still display correcly
      // without using Bing image id in current session
      deletedReview.reviewer.picture = deletedReview.reviewer.fbPictureSrc;
      delete deletedReview.reviewer.fbPictureSrc;

      dispatchSiteConfig({
        type: 'UPDATE_IMAGES',
        payload: {
          imageIdsToRemove: deletedReview.reviewer && [deletedReview.reviewer.imageId],
        },
      });

      dispatchSiteConfig({
        type: 'UPDATE_COMPONENT_BY_ID',
        payload: {
          ...componentConfig,
          reviews: selectedReviewState,
        },
      });

      const newState = [...selectedReviewState];

      return newState;
    });
  }, [componentConfig]);

  const smartPageDataContext = siteConfig.smartPageDataContext || {};
  const { PageId, UserId } = smartPageDataContext;

  useEffect(() => {
    importFromFacebook(
      siteConfig,
      smartPageService,
      facebookPageInfoFilter.ratings,
      hostSystemScenario
    )
      .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
      .then(({ reviews, businessName }) => {
        setAllFbReviews(map(reviews, review => ({
          recommendationType: review.RecommendationType,
          reviewText: review.ReviewText,
          reviewer: {
            name: review.Reviewer.Name,
            picture: review.Reviewer.Picture.Url,
          },
          createdTime: review.CreatedTime,
          businessName,
          id: `${review.CreatedTime}-${review.Reviewer.Id}`,
        })));
      })
      .catch((err) => {
        if (err) {
          if (hasFBOAuthError(err)) {
            logger.logTrace('Encounter Facebook OAuth error while importing FB reviews');
            showWarning(i18n.getString(_TL_('We could not access your Facebook profile, please reconnect it in Settings.')));
          } else if (err.message !== 'NO_FB_AUTH') {
            logger.logError(`Import reviews from facebook failed. ${err}, error string: ${JSON.stringify(err)}`);
            showWarning(i18n.getString(_TL_('We were not able to find reviews from this Facebook page. Please try again or reimport your Facebook Page.')));
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PageId, UserId]);

  return (
    <div className={classes.reviewsEditorContainer}>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Reviews'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Add up to {{max}} customer reviews from Facebook to your website.'), { max: MaxReviewsCount })}
      </div>
      <div className={classnames(classes.reviewsList, 'smart-page-reviews-list')}>
        <div className={classes.reviewsListLabel}>
          <span className={sectionTitleClassName}>{i18n.getString(_TL_('Reviews from the last 2 years'))}</span>
          <ActionButton
            className={classes.addBtn}
            disabled={noData || (selectedReviews && selectedReviews.length === MaxReviewsCount)}
            onClick={() => { setShowDialog(true); }}
          >
            {i18n.getString(_TL_('+ Add reviews'))}
          </ActionButton>
        </div>
        {
          noDataMsgKey ?
            <MessageBar className={classnames(infoMessageBarClassName, classes.noData, 'smart-page-review-no-data')} isMultiline>
              {i18n.getString(noDataMsgKey)}
            </MessageBar> :
            <ItemPicker
              items={selectedReviews}
              i18n={i18n}
              ItemComponent={SmartReviewCard}
              onDelete={onDelete}
              getDeleteButtonAriaLabel={
                index => i18n.getString(_TL_('Delete review {{number}}'), { number: index })
              }
            />
        }
      </div>
      {
        showDialog &&
        <SelectReviewDialog
          i18n={i18n}
          onAdd={onSaveChange}
          onCancel={() => { setShowDialog(false); }}
          reviews={allFbReviews}
          preselectedReviews={map(selectedReviews || [], review => review.id)}
        />
      }
    </div>
  );
};

UnStyledSmartReviewEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    reviewsListLabel: PropTypes.string.isRequired,
  }).isRequired,
};

export const SmartReviewEditor = withDefaultStyles(
  UnStyledSmartReviewEditor,
  getStyles
);
